import serverURL from "api/auth/auth";
import { clearRenterSession, getAccessToken } from "utils/renterSession";
import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import toast from "react-hot-toast";

interface ErrorResponse {
  error?: string;
  message?: string;
  detail?: string;
}

// Create an Axios instance
const customAxios: AxiosInstance = axios.create({
  baseURL: serverURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor to include Authorization token
customAxios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // sessionStorage-backed (1NOW-1557) — the renter credential must not
    // outlive the tab. Same synchronous read, so this interceptor is unchanged
    // in shape.
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle response errors globally
customAxios.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError<ErrorResponse>) => {
    if (error.response) {
      const config = error.config as
        | (InternalAxiosRequestConfig & { _retriedWithoutAuth?: boolean })
        | undefined;
      // A 401 on a request that carried a Bearer token means the stored
      // token has gone stale (expired, or the account no longer exists) —
      // the request interceptor attaches it to every call, so without
      // cleanup the renter keeps hitting 401s even on public endpoints
      // (locations, fleet, price previews). Drop the dead session and
      // replay the request once without Authorization: public endpoints
      // succeed anonymously, genuinely protected calls fail through to
      // the caller, which can re-authenticate.
      if (
        error.response.status === 401 &&
        config &&
        !config._retriedWithoutAuth &&
        config.headers?.Authorization
      ) {
        clearRenterSession();
        config._retriedWithoutAuth = true;
        delete (config.headers as any).Authorization;
        return customAxios(config);
      }
    } else {
      // Leads with the label "Network error:" previously — the renter's own
      // connection is the actionable part, so lead with that instead.
      toast.error(
        "No internet connection. Please check your connection and try again."
      );
    }

    return Promise.reject(error);
  }
);

export default customAxios;
